import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/theme';
import { useMenuStore } from '@/store/menu.store';
import {
  LayoutDashboard, Users, CreditCard, RotateCcw, KeyRound,
  FileCheck, BarChart3, GitBranch, Infinity, Scale,
  Zap, Settings2, Layers, ShieldCheck, Link as LinkIcon, Pin, PinOff,
} from 'lucide-react';

// ── Static icon map ───────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ElementType> = {
  '/dashboard':      LayoutDashboard,
  '/payments':       CreditCard,
  '/refunds':        RotateCcw,
  '/recon':          Scale,
  '/analytics':      BarChart3,
  '/mandates':       FileCheck,
  '/tokenization':   KeyRound,
  '/gateway-config': Settings2,
  '/routing':        GitBranch,
  '/users':          Users,
  '/load-tester':    Zap,
  '/payment-config': Layers,
  '/settings/2fa':   ShieldCheck,
};

interface SidebarProps {
  open: boolean;
  pinned: boolean;
  onTogglePin: () => void;
  onMouseLeave: () => void;
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

export function Sidebar({ open, pinned, onTogglePin, onMouseLeave }: SidebarProps) {
  const { theme } = useTheme();
  const menuItems = useMenuStore((s) => s.items);

  const visibleItems = menuItems.filter((m) => m.visible);
  const sections = [...new Set(visibleItems.map((i) => i.section))].map((label) => ({
    label,
    items: visibleItems.filter((i) => i.section === label),
  }));

  const allItems = visibleItems;
  const getIdx = (href: string) => allItems.findIndex((i) => i.href === href) + 1;

  return (
    <aside
      className="fixed inset-y-0 left-0 z-40 w-60 flex flex-col overflow-hidden transition-transform duration-300 ease-in-out"
      style={{
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--border)',
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        boxShadow: open && !pinned ? '4px 0 24px rgba(0,0,0,0.18)' : 'none',
      }}
      onMouseLeave={onMouseLeave}
    >
      {/* Dot-grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, var(--sidebar-sep) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Top accent line */}
      <div className="absolute top-0 inset-x-0 h-px"
        style={{
          background: theme === 'e-ink'
            ? 'rgba(0,0,0,0.2)'
            : 'linear-gradient(90deg, transparent, rgba(34,211,238,0.5), transparent)',
        }}
      />

      {/* ── Logo ──────────────────────────────────────────── */}
      <div
        className="relative flex items-center gap-3 px-5 py-5"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        {/* Logo mark */}
        <div className="relative w-9 h-9 shrink-0 flex items-center justify-center">
          {theme !== 'e-ink' && (
            <>
              <div
                className="absolute inset-0 rounded-full border animate-cyber-rotate"
                style={{ borderColor: 'rgba(34,211,238,0.25)', borderStyle: 'dashed' }}
              />
              <div
                className="absolute inset-1 rounded-full border animate-cyber-rotate-rev"
                style={{ borderColor: 'rgba(168,85,247,0.2)', borderStyle: 'dashed' }}
              />
            </>
          )}
          {theme === 'e-ink' && (
            <>
              <div
                className="absolute inset-0 rounded-full border"
                style={{ borderColor: 'rgba(0,0,0,0.18)', borderStyle: 'dashed', borderRadius: '0.25rem' }}
              />
              <div
                className="absolute inset-1 border"
                style={{ borderColor: 'rgba(0,0,0,0.1)', borderStyle: 'dashed', borderRadius: '0.125rem' }}
              />
            </>
          )}
          <div
            className="relative w-7 h-7 flex items-center justify-center"
            style={{
              background: theme === 'e-ink'
                ? '#0d0c0a'
                : 'linear-gradient(135deg, #22d3ee, #a855f7)',
              boxShadow: theme === 'e-ink' ? 'none' : '0 0 16px rgba(34,211,238,0.5), 0 0 32px rgba(34,211,238,0.2)',
              borderRadius: theme === 'e-ink' ? '0.25rem' : '50%',
            }}
          >
            <Infinity className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={`font-bold text-sm leading-none tracking-wide ${theme !== 'e-ink' ? 'animate-neon-flicker' : ''}`}
            style={{
              color: 'var(--sidebar-logo)',
              fontFamily: theme === 'e-ink' ? "'JetBrains Mono', 'Fira Code', monospace" : undefined,
              letterSpacing: theme === 'e-ink' ? '0.1em' : undefined,
            }}
          >
            SPX
          </div>
          <div
            className="text-[13px] mt-1.5 font-mono tracking-[0.22em] uppercase"
            style={{ color: 'var(--sidebar-sub)' }}
          >
            Payments Orchestration
          </div>
        </div>

        {/* Live pulse / e-ink status dot */}
        <div className="relative flex items-center gap-1 shrink-0">
          <div className="relative w-2 h-2">
            {theme === 'e-ink' ? (
              <div className="absolute inset-0 rounded-none" style={{ background: '#0d0c0a', width: 6, height: 6 }} />
            ) : (
              <>
                <div className="absolute inset-0 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px #34d399' }} />
                <div className="absolute inset-0 rounded-full bg-emerald-400 animate-pulse-ring opacity-75" />
              </>
            )}
          </div>
        </div>

        {/* Pin / Unpin button */}
        <button
          onClick={onTogglePin}
          title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
          aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
          className="shrink-0 p-1 rounded transition-all duration-200"
          style={{
            color: pinned ? 'var(--accent)' : 'var(--sidebar-text)',
            background: pinned ? 'var(--sidebar-active)' : 'transparent',
            border: pinned ? '1px solid var(--border)' : '1px solid transparent',
          }}
          onMouseEnter={(e) => {
            if (!pinned) (e.currentTarget as HTMLElement).style.color = 'var(--accent)';
          }}
          onMouseLeave={(e) => {
            if (!pinned) (e.currentTarget as HTMLElement).style.color = 'var(--sidebar-text)';
          }}
        >
          {pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
        </button>
      </div>

      {/* ── Navigation ───────────────────────────────────── */}
      <nav aria-label="Main navigation" className="flex-1 px-2.5 py-2 overflow-y-auto">
        {sections.map((section, sIdx) => (
          <div key={section.label} className={sIdx > 0 ? 'mt-0.5' : ''}>
            {/* Section header */}
            <div className="flex items-center gap-2 px-3 py-1 mb-0.5">
              <span
                className="text-[13px] font-mono font-bold tracking-[0.18em] uppercase shrink-0"
                style={{ color: 'var(--sidebar-sub)' }}
              >
                // {section.label}
              </span>
              <div className="flex-1 h-px" style={{ background: 'var(--sidebar-sep)' }} />
            </div>

            {/* Items */}
            <div className="space-y-0.5 mb-0.5">
              {section.items.map((item) => {
                const n = getIdx(item.href);
                const idx = String(n).padStart(2, '0');
                const staggerClass = `stagger-${Math.min(n, 8)}`;
                const Icon = ICON_MAP[item.href] ?? LinkIcon;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    aria-label={item.label}
                    className={({ isActive }) =>
                      cn('nav-link animate-slide-in-left', staggerClass, isActive && 'active')
                    }
                  >
                    <span
                      className="text-[13px] font-mono shrink-0 w-5"
                      style={{ color: 'var(--label-accent-dim)' }}
                    >
                      {idx}
                    </span>
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* ── System status ────────────────────────── */}
      <div className="px-2.5 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2 px-3 py-1.5">
          {theme === 'e-ink' ? (
            <div className="w-1.5 h-1.5 shrink-0" style={{ background: '#0d0c0a' }} />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" style={{ boxShadow: '0 0 5px #34d399' }} />
          )}
          <span
            className="text-[11px] font-mono tracking-[0.14em] uppercase"
            style={{ color: 'var(--sidebar-status)' }}
          >
            {theme === 'e-ink' ? '[ ONLINE ]' : 'System Online'}
          </span>
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 inset-x-0 h-px"
        style={{
          background: theme === 'e-ink'
            ? 'rgba(0,0,0,0.15)'
            : 'linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent)',
        }}
      />
    </aside>
  );
}
